/**
 * THE CODE ON THE TABLE.
 *
 * Master plan §4 M11. A guest scans a QR and sees their bill. Three properties
 * decide the design, and only one of them is about cryptography.
 *
 * 1. THE TILL MINTS IT OFFLINE. §4 M11 is explicit — "opaque POS-owned QR
 *    payload generated locally including offline" — and the reason is the
 *    venue's WAN, not the guest's. A till that had to ask a server for a code
 *    could not print one during the outage the whole offline story exists for,
 *    and a guest with a working phone would be told to wait for the
 *    restaurant's router.
 *
 *    So the token is SELF-DESCRIBING AND SIGNED: it carries which check it is
 *    and a MAC the server can check without ever having seen it before. No
 *    round trip, no table to write to, nothing to replicate.
 *
 * 2. IT IS OPAQUE TO THE GUEST. Not a check id in a URL. A guest who can read
 *    one identifier can guess the next, and the next table's bill is not theirs
 *    to see. The MAC is what makes guessing useless: without the venue key,
 *    every forgery is rejected, and the identifiers inside are meaningless
 *    without one that verifies.
 *
 * 3. IT EXPIRES. A code photographed on Tuesday must not open the same table's
 *    bill on Friday, when strangers are sitting there. The expiry is INSIDE the
 *    signed payload rather than checked against a stored record, because there
 *    is no stored record — that is the point of (1).
 *
 * WHAT IT IS NOT: a capability to pay. Resolving a token yields a READ of one
 * check. Paying carries its own idempotency key and its own server-side
 * re-pricing, because "the thing that shows you a bill" and "the thing that
 * takes your money" must not be the same permission.
 */

import { fromBase64Url, toBase64Url } from "@gf/kernel/crypto";

/** What the code carries, and all it carries. */
export interface GuestTokenClaims {
  readonly venueId: string;
  readonly orderId: string;
  readonly checkId: string;
  /** Milliseconds since the epoch, from the till's clock. */
  readonly issuedAt: number;
  /** Milliseconds since the epoch. Inside the MAC, so it cannot be extended. */
  readonly expiresAt: number;
  /**
   * Thirty-two bits that make two codes for the same check different.
   *
   * Without it, re-printing would produce a byte-identical code, and a guest who
   * kept the first one would hold a working key to a bill that has since been
   * reopened. It also stops a scanner's cache answering from yesterday.
   *
   * A number rather than a uuid because four bytes is what a QR can afford —
   * see `payloadOf`. It is not a secret and carries no entropy requirement: the
   * MAC is what makes a code unforgeable, this only makes it unique.
   */
  readonly nonce: number;
}

export type GuestTokenFailure =
  | "MALFORMED"
  | "BAD_SIGNATURE"
  | "EXPIRED"
  /** The code is for a different venue — a scanner pointed at the wrong QR. */
  | "WRONG_VENUE";

export type GuestTokenResult =
  | { readonly ok: true; readonly claims: GuestTokenClaims }
  | { readonly ok: false; readonly reason: GuestTokenFailure };

/**
 * How long a code is good for.
 *
 * Four hours: longer than any meal and shorter than a shift, so a code
 * photographed at lunch is dead by dinner. Re-scanning the physical QR gets a
 * fresh one, because the till mints on demand.
 */
export const GUEST_TOKEN_TTL_MS = 4 * 3_600_000;

/**
 * How much clock skew is forgiven on the ISSUED side.
 *
 * A till's clock is the thing that cannot be trusted — that rule runs through
 * this whole repository. A token from thirty seconds in the future is a tablet
 * that is slightly fast, not an attack; one from an hour ahead is neither, and
 * is refused. The EXPIRY side gets no such grace: erring toward closing a
 * guest's window early is the safe direction.
 */
const FUTURE_SKEW_MS = 60_000;

const encoder = new TextEncoder();

async function key(secret: string): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    "raw",
    encoder.encode(secret),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign", "verify"],
  );
}

/**
 * THE PAYLOAD IS BINARY, AND THAT IS A PRODUCT DECISION RATHER THAN A SAVING.
 *
 * The first version joined the fields with pipes and base64url'd the text: three
 * hyphenated uuids, two millisecond timestamps and a uuid nonce came to 175
 * characters, 234 after encoding, 303 once the MAC and the URL were on it — and
 * the QR encoder refused it outright, because 303 bytes does not fit in a
 * version-10 code at level L.
 *
 * Which was the right answer to the wrong question. A shorter code is not a
 * smaller file, it is a code that SCANS FROM FURTHER AWAY AND IN WORSE LIGHT,
 * on a table, with a phone held at an angle, by somebody who will give up after
 * two tries. So the same facts go in as bytes:
 *
 *   venue · order · check   16 bytes each, the uuids themselves
 *   issued · expires         6 bytes each, milliseconds — good to the year 10889
 *   nonce                    4 bytes
 *   MAC                     16 bytes, truncated from SHA-256
 *
 * 80 bytes, 108 base64url characters, a version-5 code. Roughly a third of the
 * modules of the version 10 that did not fit.
 *
 * TRUNCATING THE MAC TO 128 BITS is standard practice (RFC 2104 §5 permits it
 * explicitly, and JWT's HS256 is the outlier in not doing it). The attack it has
 * to resist is forging a code for a bill in a restaurant, against a key that
 * rotates with the platform secret; 2^128 is not the weak link in that chain.
 */
const PAYLOAD_BYTES = 64;
const MAC_BYTES = 16;

const uuidToBytes = (uuid: string): Uint8Array => {
  const hex = uuid.replaceAll("-", "");
  if (hex.length !== 32) throw new Error(`not a uuid: ${uuid}`);
  const out = new Uint8Array(16);
  for (let i = 0; i < 16; i++) out[i] = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return out;
};

const bytesToUuid = (bytes: Uint8Array): string => {
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

/** Six bytes, big-endian. Milliseconds fit until long after any of this matters. */
const writeMillis = (into: Uint8Array, at: number, value: number): void => {
  let v = value;
  for (let i = 5; i >= 0; i--) {
    into[at + i] = v % 256;
    v = Math.floor(v / 256);
  }
};

const readMillis = (from: Uint8Array, at: number): number => {
  let v = 0;
  for (let i = 0; i < 6; i++) v = v * 256 + from[at + i]!;
  return v;
};

function payloadOf(claims: GuestTokenClaims): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(PAYLOAD_BYTES);
  out.set(uuidToBytes(claims.venueId), 0);
  out.set(uuidToBytes(claims.orderId), 16);
  out.set(uuidToBytes(claims.checkId), 32);
  writeMillis(out, 48, claims.issuedAt);
  writeMillis(out, 54, claims.expiresAt);
  new DataView(out.buffer).setUint32(60, claims.nonce >>> 0);
  return out;
}

export interface MintArgs {
  readonly venueId: string;
  readonly orderId: string;
  readonly checkId: string;
  readonly secret: string;
  readonly now: Date;
  readonly nonce: number;
  readonly ttlMs?: number;
}

/** Mint a code. Pure apart from the MAC; no network, no storage. */
export async function mintGuestToken(args: MintArgs): Promise<string> {
  const claims: GuestTokenClaims = {
    venueId: args.venueId,
    orderId: args.orderId,
    checkId: args.checkId,
    issuedAt: args.now.getTime(),
    expiresAt: args.now.getTime() + (args.ttlMs ?? GUEST_TOKEN_TTL_MS),
    nonce: args.nonce,
  };

  const payload = payloadOf(claims);
  const mac = new Uint8Array(await crypto.subtle.sign("HMAC", await key(args.secret), payload));

  // One base64url string, no separator: the layout is fixed, so a delimiter
  // would be a character spent on nothing.
  const token = new Uint8Array(PAYLOAD_BYTES + MAC_BYTES);
  token.set(payload, 0);
  token.set(mac.subarray(0, MAC_BYTES), PAYLOAD_BYTES);
  return toBase64Url(token);
}

export interface VerifyArgs {
  readonly token: string;
  readonly secret: string;
  readonly now: Date;
  /** Refuse a code minted for a different venue, even if it verifies. */
  readonly venueId?: string;
}

/**
 * Check a code and say what it is for.
 *
 * ORDER MATTERS: the MAC is checked BEFORE anything inside is believed. Reading
 * the expiry out of an unverified payload and refusing on it would leak whether
 * a forged token's *structure* was right, and would let a forger learn the
 * format by watching which lie he told produced which refusal.
 */
export async function verifyGuestToken(args: VerifyArgs): Promise<GuestTokenResult> {
  let raw: Uint8Array<ArrayBuffer>;
  try {
    raw = fromBase64Url(args.token);
  } catch {
    return { ok: false, reason: "MALFORMED" };
  }

  // A fixed layout, so a wrong length is wrong before anything is read out of
  // it. Checked first because everything below indexes into it.
  if (raw.length !== PAYLOAD_BYTES + MAC_BYTES) return { ok: false, reason: "MALFORMED" };

  const payload = raw.subarray(0, PAYLOAD_BYTES);
  const mac = raw.subarray(PAYLOAD_BYTES);

  /**
   * The MAC is checked BEFORE anything inside is believed.
   *
   * Reading the expiry out of an unverified payload and refusing on it would
   * leak whether a forgery's structure was right, and let a forger learn the
   * format by watching which lie produced which refusal.
   *
   * Compared byte by byte in CONSTANT TIME. `crypto.subtle.verify` will not do
   * it here because the MAC is truncated and it expects the full digest, so the
   * comparison is explicit — and it must not short-circuit, which is why it
   * accumulates a difference rather than returning early.
   */
  const expected = new Uint8Array(
    await crypto.subtle.sign("HMAC", await key(args.secret), payload),
  ).subarray(0, MAC_BYTES);

  let difference = 0;
  for (let i = 0; i < MAC_BYTES; i++) difference |= mac[i]! ^ expected[i]!;
  if (difference !== 0) return { ok: false, reason: "BAD_SIGNATURE" };

  const venueId = bytesToUuid(payload.subarray(0, 16));
  const orderId = bytesToUuid(payload.subarray(16, 32));
  const checkId = bytesToUuid(payload.subarray(32, 48));
  const issued = readMillis(payload, 48);
  const expires = readMillis(payload, 54);
  const nonce = new DataView(payload.buffer, payload.byteOffset).getUint32(60);

  const now = args.now.getTime();
  if (expires <= now) return { ok: false, reason: "EXPIRED" };
  // A token from the future is a till whose clock is wrong. A minute of grace;
  // an hour is not a fast tablet.
  if (issued > now + FUTURE_SKEW_MS) return { ok: false, reason: "EXPIRED" };

  if (args.venueId !== undefined && args.venueId !== venueId) {
    return { ok: false, reason: "WRONG_VENUE" };
  }

  return {
    ok: true,
    claims: { venueId, orderId, checkId, issuedAt: issued, expiresAt: expires, nonce },
  };
}

/**
 * The per-venue key, derived rather than stored.
 *
 * One platform secret, one key per venue, and a venue's key tells you nothing
 * about the next one's. It means there is no key table to provision, rotate or
 * leak — rotating the platform secret rotates every venue at once, which is the
 * blunt instrument you want in the case where you need it.
 *
 * The till receives its own key inside the SIGNED CONFIG BUNDLE, so it can mint
 * offline without ever holding the platform secret.
 */
export async function venueGuestKey(platformSecret: string, venueId: string): Promise<string> {
  const mac = await crypto.subtle.sign(
    "HMAC",
    await key(platformSecret),
    encoder.encode(`guest-token|${venueId}`),
  );
  return toBase64Url(mac);
}
